import { STORE_LOCATOR_CLOSE_INFOWINDOW_BUTTON } from '@/scripts/constants/css-classes'
import {
  MARKER_ICON_HEIGHT,
  MARKER_ICON_WIDTH,
  RAYBAN_STORES_MARKER_ICON_WIDTH,
} from '@/scripts/constants/physical-stores'
import { EVENTS } from '../constants/css-classes'
import {
  GENERAL,
  RAYBAN_STORES,
  STORE_LOCATOR_BLACK_CLUSTERING_ICON,
  STORE_LOCATOR_RED_MAP_CLUSTERING_ICON,
} from '../constants/store-locator'

// eslint-disable-next-line no-undef
const microsoftMaps = window.Microsoft?.Maps ?? {}

export const initializeBingMaps = (containerId) => {
  const map = new microsoftMaps.Map(containerId)
  window.bing = { maps: map }
  return map
}

export const geocodeByCountryCode = (map, where, callback) => {
  microsoftMaps.loadModule('Microsoft.Maps.Search', function () {
    const searchManager = new microsoftMaps.Search.SearchManager(map)
    const requestOptions = {
      where,
      callback,
    }
    searchManager.geocode(requestOptions)
  })
}

const handleBingTooltipClick = (e) => {
  const isCloseClick = e.originalEvent.target.className.includes(
    STORE_LOCATOR_CLOSE_INFOWINDOW_BUTTON
  )
  if (isCloseClick) {
    e.target.setOptions({ visible: false })
  }
}

export const initializeBingTooltip = (map) => {
  const tooltip = new microsoftMaps.Infobox(map.getCenter(), {
    visible: false,
    autoAlignment: true,
  })
  tooltip.setMap(map)

  microsoftMaps.Events.addHandler(tooltip, EVENTS.CLICK, handleBingTooltipClick)

  return tooltip
}

export const getBingPlacesAutocomplete = (
  options,
  inputId,
  inputContainerId,
  suggestionsCallback
) => {
  microsoftMaps.loadModule('Microsoft.Maps.AutoSuggest', () => {
    const manager = new microsoftMaps.AutosuggestManager(options)
    manager.attachAutosuggest(inputId, inputContainerId, suggestionsCallback)
  })
}

export const getBingAddressFromCoordinates = (latitude, longitude, addressCallback) => {
  microsoftMaps.loadModule('Microsoft.Maps.Search', () => {
    const manager = new microsoftMaps.Search.SearchManager(window?.bing?.maps)
    manager.reverseGeocode({
      location: new microsoftMaps.Location(+latitude, +longitude),
      callback: addressCallback,
    })
  })
}

export const drawMarkersWithClusteringOnBingMap = (
  mapInstance,
  storesList,
  clusterType = GENERAL
) => {
  const markers = []

  storesList.forEach((store) => {
    markers.push(initializeBingMarker(store))
  })

  microsoftMaps.loadModule('Microsoft.Maps.Clustering', () => {
    const clusterLayer = new microsoftMaps.ClusterLayer(markers, {
      clusteredPinCallback: (cluster) => {
        createCustomClusteredPin(cluster, mapInstance, clusterType)
      },
      gridSize: 80,
    })
    mapInstance.layers.insert(clusterLayer)
  })

  return markers
}

const onClusterClick = (e, mapInstance) => {
  if (e.target.containedPushpins) {
    const locs = []
    for (let i = 0, len = e.target.containedPushpins.length; i < len; i++) {
      locs.push(e.target.containedPushpins[i].getLocation())
    }

    const bounds = microsoftMaps.LocationRect.fromLocations(locs)

    // TODO: include this in one of the functions for centering the map
    mapInstance.setView({ bounds: bounds, padding: 100 })
  }
}

const createCustomClusteredPin = (cluster, mapInstance, clusterType = GENERAL) => {
  microsoftMaps.Events.addHandler(cluster, EVENTS.CLICK, (e) => {
    onClusterClick(e, mapInstance)
  })
  cluster.setOptions({
    icon:
      clusterType === RAYBAN_STORES
        ? STORE_LOCATOR_RED_MAP_CLUSTERING_ICON
        : STORE_LOCATOR_BLACK_CLUSTERING_ICON,
  })
}

export const initializeBingMarker = (store) => {
  const markerLocation = new microsoftMaps.Location(store.latitude, store.longitude)

  const marker = new microsoftMaps.Pushpin(markerLocation, {
    icon: store.markerIcon,
    iconSize: {
      width:
        store.storeType === RAYBAN_STORES ? RAYBAN_STORES_MARKER_ICON_WIDTH : MARKER_ICON_WIDTH,
      height:
        store.storeType === RAYBAN_STORES ? RAYBAN_STORES_MARKER_ICON_WIDTH : MARKER_ICON_HEIGHT,
    },
    roundClickableArea: true,
  })

  marker.uniqueID = store.uniqueID
  return marker
}

export const drawMarkerOnBingMap = (mapInstance, marker) => {
  mapInstance.entities.push(marker)
  return marker
}

export const clearAllBingMapsMarkers = (bingMarkers, bingMapReference) => {
  bingMarkers.forEach((marker) => bingMapReference.entities.remove(marker))
}

export const clearBingMapsEventHandlers = (mapInstance, eventHandlers) => {
  if (!!eventHandlers && Array.isArray(eventHandlers) && eventHandlers.length > 0) {
    eventHandlers.forEach((handler) => mapInstance.Events.removeHandler(handler))
  }
}

export const centerBingMapUsingCoordinates = (mapInstance, latitude, longitude) => {
  mapInstance.setView({
    center: new microsoftMaps.Location(latitude, longitude),
  })
}

export const centerBingMapUsingLocation = (mapInstance, bingLocationObject) => {
  mapInstance.setView({ center: bingLocationObject })
}

export const centerBingMapUsingBounds = (mapInstance, bingBoundsObject) => {
  mapInstance.setView({ bounds: bingBoundsObject })
}

export const triggerEventOnBingEntity = (bingEntity, eventType) => {
  // eslint-disable-next-line no-undef
  Microsoft.Maps.Events.invoke(bingEntity, eventType)
}
